// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// O texto do mapa — lógica pura
// ============================================================================
//
// Tudo aqui é função pura dos dados. Nenhuma leitura de DOM, nenhum estado. São
// as regras de leitura do mapa, e dentro de um componente só se verificariam a
// olho.
//
// AS TRÊS BANDAS DE UMA LINHA
//   1 · barra do itinerário — cliente, escrito UMA vez por corrida
//   2 · localidade do dia   — muda todos os dias, por isso é por dia
//   3 · barra do motorista  — nome, escrito UMA vez por corrida
//
//   São as três sublinhas que as planeadoras conhecem. O que mudou é que duas
//   delas deixaram de repetir o mesmo texto em cada célula: repetir `IGT` oito
//   vezes seguidas é ruído, e era a principal razão de o mapa ser ilegível.
//
// O DIA DE ROTAÇÃO É A EXCEPÇÃO
//   Nesse dia as bandas não se aplicam: a célula mostra a **chegada** e a
//   **partida**, e não é escolha nossa — é o que os planeadores já fazem (126
//   células medidas). Ver `resumoRotacao`.
// ============================================================================

import { BANDEIRA, type CelulaCompacta, type DadosMapa } from '@xlv-gva-mpt/dominio';

/**
 * Extrai `HH:MM` de um texto de movimento.
 *
 * O gerador escreve `in 19:25`; a folha real escreve também `BCN out 07:10`,
 * `MAD out ave 8:50`, `BCN barco 9:30`. O padrão comum é «uma hora em algum sítio
 * do texto», e é isso que se procura — não uma posição fixa, que quebraria no
 * primeiro formato diferente.
 *
 * Devolve `''` quando não há hora: um texto sem hora é informação legítima
 * (`Vazio`, `Volvo`), não um erro.
 */
export function horaDe(texto: string): string {
  const m = /(\d{1,2}):(\d{2})/.exec(texto);
  if (m === null) return '';
  return `${m[1]?.padStart(2, '0') ?? '00'}:${m[2] ?? '00'}`;
}

/**
 * Tempo de rotação em minutos: da chegada à partida no mesmo dia. Requisito V4.
 *
 * ⚠️ Pode ser NEGATIVO, e isso não é defeito deste código. A camada visual §4
 * documenta o caso medido da viatura 216: chegada às 18:30 e partida às 13:10 do
 * mesmo dia. Ou são viagens diferentes, ou é erro de registo — em qualquer caso é
 * algo que o mapa deve mostrar, não corrigir em silêncio.
 */
export function tempoRotacaoMinutos(movimento: readonly [string, string] | null): number | null {
  if (movimento === null) return null;
  const chegada = horaDe(movimento[0]);
  const partida = horaDe(movimento[1]);
  if (chegada === '' || partida === '') return null;
  const min = (h: string): number => {
    const [hh, mm] = h.split(':');
    return Number(hh) * 60 + Number(mm);
  };
  return min(partida) - min(chegada);
}

/** `2h00`, `45min`, `-5h20`. */
export function duracao(minutos: number): string {
  const s = minutos < 0 ? '-' : '';
  const m = Math.abs(minutos);
  return m < 60 ? `${s}${m}min` : `${s}${Math.floor(m / 60)}h${String(m % 60).padStart(2, '0')}`;
}

/**
 * Meios que merecem ícone. Requisito V8: ferry e comboio, além do voo.
 *
 * ⚠️ **`comboio` e não `comboio_ave`.** A base de dados guarda a categoria — `comboio`
 * — e a informação que a lei pede num campo próprio: `com_cabine`, porque o art. 9.º
 * n.º 2 faz depender o repouso de haver cabine, não da marca do comboio.
 *
 * `comboio_ave` continua aceite porque é o que os CSV do gerador produzem, e a leitura
 * dos CSV é o caminho que serve o mapa quando não há base de dados. **Sem esta linha, a
 * mesma viagem tinha ícone de comboio numa origem e nenhum na outra** — e isso foi
 * apanhado a comparar as duas célula a célula, não a olhar para o ecrã.
 */
export type MeioComIcone = 'voo' | 'comboio' | 'ferry';

/**
 * Que ícone mostrar para um meio de deslocação, ou `null`.
 *
 * Devolve a **chave** e não o desenho: quem desenha é `icones.tsx`, com SVG de
 * cor única. A versão anterior devolvia emoji, que trazem cor própria para dentro
 * de um mapa onde a cor tem significado, e mudam de forma de sistema para sistema.
 *
 * `estrada` e desconhecidos não levam ícone: um ícone em quase todas as células
 * deixa de distinguir o que quer que seja. Medido: `estrada` são 16 598 das
 * 90 208 alocações.
 */
export function meioComIcone(meio: string): MeioComIcone | null {
  if (meio === 'voo' || meio === 'ferry') return meio;
  // As duas escritas do mesmo meio: a da base (`comboio`) e a dos CSV (`comboio_ave`).
  if (meio === 'comboio' || meio === 'comboio_ave') return 'comboio';
  return null;
}

/**
 * Rótulo da barra do itinerário.
 *
 * `sigla` sempre — é a identidade (V3). `nome` só quando a barra é larga o
 * suficiente para o mostrar sem cortar: mostrar meia palavra é pior do que não a
 * mostrar. O limiar em dias, e não em px, porque é o que o chamador conhece.
 */
export function rotuloItinerario(
  dados: DadosMapa,
  cliente: number,
  dias: number,
): { readonly sigla: string; readonly nome: string } {
  const c = cliente >= 0 ? dados.clientes[cliente] : undefined;
  const sigla = c?.[0] ?? '—';
  // 4 dias ≈ 160 px. Abaixo disso só cabe a sigla.
  const nome = dias >= 4 ? (c?.[1] ?? '') : '';
  return { sigla, nome };
}

/**
 * Rótulo da barra do motorista.
 *
 * Na tripulação dupla mostram-se **os dois** quando cabem. Dizer só um nome
 * esconderia que o regime legal de repouso é outro (art. 8.º n.º 5).
 */
export function rotuloMotorista(
  dados: DadosMapa,
  motorista: number,
  motorista2: number,
  dias: number,
): string {
  const a = motorista >= 0 ? (dados.motoristas[motorista] ?? '') : '';
  if (motorista2 < 0) return a;
  const b = dados.motoristas[motorista2] ?? '';
  return dias >= 3 ? `${a} + ${b}` : `${a} +`;
}

/**
 * O dia na banda da localidade: o texto e o sentido do movimento, separados.
 *
 * Separados de propósito. A versão anterior concatenava `↓` ao texto, o que
 * obrigava a cor e o tamanho da seta a serem os do texto e impedia usar um ícone
 * de traço. Agora quem desenha decide como mostrar o sentido.
 */
export function rotuloDia(
  dados: DadosMapa,
  celula: CelulaCompacta,
): {
  readonly local: string;
  /**
   * Onde os PASSAGEIROS entram, no primeiro dia da viagem. `''` nos outros dias.
   *
   * Um dia pode ter DOIS locais: onde o grupo embarca e onde se pernoita depois
   * do primeiro dia — e não é o mesmo sítio. Medido na folha real: **741 de
   * 15 940 células de localidade** trazem o padrão `A/B`, **4,6 %**. Foi o
   * cliente a apontá-lo.
   */
  readonly embarque: string;
  readonly sentido: 'chegada' | 'partida' | null;
} {
  // Na grelha é o CÓDIGO: é o que cabe. O nome completo vai para o painel.
  const local = codigoLocal(dados, celula[1]);
  const embarque = codigoLocal(dados, celula[9]);
  const b = celula[4];
  const sentido =
    (b & BANDEIRA.MOVIMENTO_IN) !== 0
      ? ('chegada' as const)
      : (b & BANDEIRA.MOVIMENTO_OUT) !== 0
        ? ('partida' as const)
        : null;
  return { local, embarque, sentido };
}

/**
 * `MAD` — o código, que é o que cabe numa coluna de 40 px.
 *
 * A grelha usa o código; o painel e o cartão usam `nomeLocal`, com o nome completo.
 */
export function codigoLocal(dados: DadosMapa, indice: number): string {
  return indice >= 0 ? (dados.localidades[indice]?.[0] ?? '') : '';
}

/**
 * `MAD — Madrid`, ou só o código quando não há nome.
 *
 * ⚠️ Pedido do cliente: *«nos detalhes quero que apareça, para além das 3 letras de
 * definição do local, o nome completo, para não existirem dúvidas»*. E a dúvida é
 * real, não hipotética: na folha há `SLM` Salamanca ao lado de `SVQ` Sevilha, `BJZ`
 * Badajoz ao lado de `BIO` Bilbao, e códigos internos que não são UN/LOCODE nenhum.
 */
export function nomeLocal(dados: DadosMapa, indice: number): string {
  const l = dados.localidades[indice];
  if (l === undefined) return '';
  return l[1] === '' || l[1] === l[0] ? l[0] : `${l[0]} — ${l[1]}`;
}

/**
 * As duas horas do dia de rotação: chegada em cima, partida em baixo.
 *
 * Só as horas — o sentido é dado por ícone, não por um caractere de seta colado
 * ao texto. Vazio quando a hora não existe, para o desenho não inventar valores.
 */
export function resumoRotacao(
  movimento: readonly [string, string] | null,
): { readonly chegada: string; readonly partida: string } {
  return {
    chegada: movimento === null ? '' : horaDe(movimento[0]),
    partida: movimento === null ? '' : horaDe(movimento[1]),
  };
}

/**
 * Resumo curto, para o cartão que segue o cursor.
 *
 * Três a cinco linhas: o que responde a «que serviço é este?» sem obrigar a abrir
 * o painel. O detalhe completo está em `detalhe`.
 */
export function resumo(dados: DadosMapa, viatura: number, dia: number): readonly string[] {
  const celula = dados.grelha[viatura]?.[dia];
  const v = dados.viaturas[viatura];
  const data = new Date(Date.UTC(dados.ano, 0, 1 + dia)).toISOString().slice(0, 10);
  const linhas: string[] = [`${v?.numero ?? '?'} · ${data}`];

  if (celula == null) {
    linhas.push('sem serviço');
    return linhas;
  }

  const [cliente, localidade, motorista, , bandeiras, itinerario, motorista2, estadoViatura] =
    celula;

  if (estadoViatura >= 0) {
    linhas.push(dados.estadosViatura[estadoViatura] ?? '');
    if (localidade >= 0) linhas.push(nomeLocal(dados, localidade));
    return linhas;
  }

  if (cliente >= 0) {
    const c = dados.clientes[cliente];
    linhas.push(`${c?.[0] ?? ''} — ${c?.[1] ?? ''}`);
  }
  if (itinerario >= 0) linhas.push(dados.itinerarios[itinerario] ?? '');
  if (celula[10] >= 0) linhas.push(`reserva ${dados.corridas[celula[10]] ?? ''}`);
  if (celula[9] >= 0) {
    // Dois locais: entrada dos passageiros e pernoita. Ver `rotuloDia`.
    linhas.push(
      `entram em ${nomeLocal(dados, celula[9])} · dormem em ` +
        `${localidade >= 0 ? nomeLocal(dados, localidade) : 'local por definir'}`,
    );
  } else {
    // ⚠️ A pernoita aparece SEMPRE, mesmo em falta. Foi o cliente a exigi-lo: «o
    // pernoitar interessa para o motorista e tem de ser definido em todos os dias
    // das viagens e dias de descanso fora de casa, e tem de ficar sempre presente
    // nos detalhes, uma vez que por vezes não é indicado».
    linhas.push(
      localidade >= 0
        ? `dormem em ${nomeLocal(dados, localidade)}`
        : 'pernoita POR DEFINIR',
    );
  }
  if (motorista >= 0) {
    linhas.push(
      motorista2 >= 0
        ? `${dados.motoristas[motorista] ?? ''} + ${dados.motoristas[motorista2] ?? ''} (dupla)`
        : (dados.motoristas[motorista] ?? ''),
    );
  }
  if ((bandeiras & BANDEIRA.RESERVA) !== 0) linhas.push('RESERVA');
  return linhas;
}

/**
 * Detalhe completo, para o painel.
 *
 * Devolve pares para o painel os poder desenhar como lista de definições, em vez
 * de um bloco de texto — que era o que estava e que ninguém lê.
 */
export function detalhe(
  dados: DadosMapa,
  viatura: number,
  dia: number,
  minutosRotacaoMinimo: number,
): readonly (readonly [string, string])[] {
  const celula = dados.grelha[viatura]?.[dia];
  const v = dados.viaturas[viatura];
  const data = new Date(Date.UTC(dados.ano, 0, 1 + dia));
  const iso = data.toISOString().slice(0, 10);
  const semana = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'][
    data.getUTCDay()
  ];

  const pares: [string, string][] = [
    ['viatura', `${v?.numero ?? '?'} · ${v?.matricula ?? '?'} · ${v?.lotacao ?? '?'} lugares`],
    ['data', `${iso} (${semana})`],
  ];

  if (celula == null) {
    pares.push(['serviço', 'nenhum — viatura livre neste dia']);
    return pares;
  }

  const [cliente, localidade, motorista, estado, bandeiras, itinerario, motorista2, estadoViatura, meio] =
    celula;

  if (estadoViatura >= 0) {
    pares.push(['estado da viatura', dados.estadosViatura[estadoViatura] ?? '?']);
  }
  if (cliente >= 0) {
    const c = dados.clientes[cliente];
    pares.push(['cliente', `${c?.[0] ?? ''} — ${c?.[1] ?? ''}`]);
  }
  if (itinerario >= 0) pares.push(['rota', dados.itinerarios[itinerario] ?? '']);
  if (celula[10] >= 0) {
    // A CORRIDA identifica a viagem; a rota é só a assinatura origem/destino.
    // Medido: 2 679 assinaturas para 15 132 corridas.
    pares.push(['referência da viagem', dados.corridas[celula[10]] ?? '']);
  }
  // ---- O PRIMEIRO DIA DE UMA VIAGEM TEM DE DIZER TRÊS COISAS ---------------
  //
  // Exigência do cliente: *«em todas as novas viagens tem de ser indicado o local de
  // partida e hora, e ainda o local de pernoitar (relativo sempre ao motorista)»*.
  //
  // As três aparecem **mesmo quando faltam**, escritas como lacuna. Esconder um
  // campo em falta é garantir que ninguém o preenche — e estes três não são
  // decorativos: a hora é a do voo que o autocarro vai esperar, e a pernoita decide
  // se o repouso é legal (art. 8.º n.º 8).
  const movimento = dados.movimentos[viatura]?.[dia] ?? null;
  const primeiroDia = (bandeiras & BANDEIRA.PRIMEIRO_DIA) !== 0 && estadoViatura < 0;
  if (primeiroDia) {
    pares.push([
      'entrada dos passageiros',
      celula[9] >= 0
        ? nomeLocal(dados, celula[9])
        : localidade >= 0
          ? `${nomeLocal(dados, localidade)} (o mesmo do primeiro destino)`
          : 'POR DEFINIR',
    ]);
    const hora = horaDe(movimento?.[0] ?? '');
    pares.push([
      'hora de chegada do grupo',
      hora !== '' ? hora : 'POR DEFINIR — é a hora a que o voo aterra',
    ]);
  }

  if (localidade >= 0 && !primeiroDia) {
    pares.push(['local do dia', nomeLocal(dados, localidade)]);
  }

  // ---- A PERNOITA APARECE SEMPRE ------------------------------------------
  //
  // *«O pernoitar interessa para o motorista e tem de ser definido em todos os dias
  // das viagens e dias de descanso fora de casa, e tem de ficar sempre presente nos
  // detalhes, uma vez que por vezes não é indicado.»*
  //
  // Duas consequências no que se escreve:
  //
  // 1. **É do MOTORISTA, não da viatura.** O rótulo di-lo, porque a distinção tem
  //    efeito legal: é o repouso dele que a lei regula (art. 8.º), e a GVA é mais
  //    restritiva que a lei — o motorista **nunca** dorme no autocarro (D-060), logo
  //    toda a pernoita fora de casa é alojamento pago.
  // 2. **Aparece em falta.** Medido: 5,9 % dos dias dentro de uma corrida não têm
  //    localidade na folha real. Um campo que desaparece quando está vazio nunca é
  //    preenchido.
  //
  // ⚠️ Nos dias de oficina/avaria a viatura está parada mas o motorista **não está
  // com ela** — está em casa ou noutra viatura (D-055). A pernoita desses dias
  // pertence à linha de tempo do MOTORISTA, que o mapa da viatura não tem (P-09).
  // Diz-se isso em vez de inventar um local.
  if (estadoViatura >= 0) {
    pares.push([
      'pernoita do motorista',
      'não é desta viatura — nestes dias o motorista está em casa ou noutra viatura',
    ]);
  } else {
    const naBase = localidade >= 0 && localidade === dados.localidadeBase;
    pares.push([
      'pernoita do motorista',
      localidade < 0
        ? 'POR DEFINIR — obrigatório: decide se o repouso é legal (art. 8.º n.º 8)'
        : naBase
          ? `${nomeLocal(dados, localidade)} — na base, o motorista vai a casa: sem alojamento a pagar`
          : `${nomeLocal(dados, localidade)} — fora da base: alojamento pago`,
    ]);
  }

  if (motorista >= 0) {
    pares.push(['motorista', dados.motoristas[motorista] ?? '']);
    if (motorista2 >= 0) {
      // «tripulação dupla» e não «2.º motorista»: a diferença muda o regime legal
      // de repouso, e quem lê o mapa tem de a ver.
      pares.push([
        '2.º condutor',
        `${dados.motoristas[motorista2] ?? ''} — tripulação dupla (art. 8.º n.º 5)`,
      ]);
    }
  } else if (estadoViatura < 0) {
    pares.push(['motorista', 'por atribuir']);
  } else {
    pares.push(['motorista', 'em casa ou noutra viatura']);
  }

  if (movimento !== null) {
    // A chegada do primeiro dia já foi dita acima, com o nome «hora de chegada do
    // grupo». Repeti-la aqui punha a mesma hora duas vezes no mesmo painel.
    if (movimento[0] !== '' && !primeiroDia) pares.push(['chegada', movimento[0]]);
    if (movimento[1] !== '') pares.push(['partida', movimento[1]]);
  } else if ((bandeiras & BANDEIRA.ULTIMO_DIA) !== 0 && estadoViatura < 0) {
    // O último dia é o da partida do grupo, e a hora é a do voo. Se falta, diz-se.
    pares.push(['partida', 'POR DEFINIR — último dia da viagem']);
  }

  if ((bandeiras & BANDEIRA.ROTACAO_SUSPEITA) !== 0) {
    // Foi o cliente a apanhar: «tem um dia livre, não tem in nesse dia de nenhum
    // itinerário». Mede-se em 14,3 % dos dias que vinham marcados como rotação.
    pares.push([
      'dia de rotação',
      'marcado na origem, mas SEM partida no dia seguinte — não é rotação',
    ]);
  }

  if ((bandeiras & BANDEIRA.ROTACAO) !== 0) {
    const minutos = tempoRotacaoMinutos(movimento);
    if (minutos === null) {
      pares.push(['dia de rotação', 'sim — tempo não calculável']);
    } else if (minutos < 0) {
      // ⚠️ ISTO NÃO É ANOMALIA, e dizer que era foi erro nosso.
      //
      // MEDIDO na folha real, nas 227 caixas pretas com as duas horas legíveis:
      // **53 (23 %) têm a partida antes da chegada**. Exemplo real, viatura 216:
      // `MAD in 18:30` com `MAD out 14:25`. Marcar 23 % dos casos como «verificar»
      // é ensinar o planeador a ignorar os avisos.
      //
      // A margem que interessa é o intervalo ENTRE os dois acontecimentos, e ele
      // existe nas duas ordens — 4h05, no exemplo acima.
      //
      // ⚠️ O que fica genuinamente em aberto é OUTRA coisa, e é do domínio: a que
      // grupo pertence cada hora — se são os dois voos do dia (um parte, outro
      // aterra) ou a entrada e a saída da própria viatura na cidade. As duas
      // leituras explicam os 77 % e não se distinguem pela folha. É a pergunta
      // D-084, e não se fecha aqui.
      pares.push([
        'dia de rotação',
        `${duracao(-minutos)} entre os dois — a partida é a primeira` +
          (-minutos < minutosRotacaoMinimo ? ' — abaixo do mínimo configurado' : ''),
      ]);
    } else {
      pares.push([
        'dia de rotação',
        `${duracao(minutos)} de rotação` +
          (minutos < minutosRotacaoMinimo ? ' — abaixo do mínimo configurado' : ''),
      ]);
    }
  }

  if (meio >= 0) pares.push(['meio', dados.meios[meio] ?? '']);
  pares.push([
    'registo',
    (bandeiras & BANDEIRA.RESERVA) !== 0 ? 'RESERVA — não confirmado' : 'definitivo',
  ]);
  if ((bandeiras & BANDEIRA.DUVIDA) !== 0) pares.push(['marcado', 'provisório']);
  pares.push(['nível', dados.estados[estado] ?? '']);

  return pares;
}
